import { find, has, map } from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { Script } from "gatsby";

import ErrorBoundary from "~/components/ErrorBoundary";

import useSiteMetadata from "~/hooks/useSiteMetadata";

import { isBrowser } from "~/utilities/helpers";
import { useShouldShowMultiview } from "~/components/buttons/CADModelButton";

const Seo = ({
    pageTitle = "",
    seo = {},
    cardCopy = "",
    featuredImage = {},
    location = {
        pathname: "",
    },
    productUrl = "",
    urlPermutations = [],
    languageCode = "en",
}) => {
    const { siteUrl, companyLogo } = useSiteMetadata();
    const showMultiviewCadModel = useShouldShowMultiview();

    const seoInfo = seo ? seo : {};

    const url = `${siteUrl}${productUrl ? productUrl : location.pathname}`;

    const title = seoInfo.title ? seoInfo.title : pageTitle;

    const seoDescription = seoInfo.metaDesc ? seoInfo.metaDesc : cardCopy;

    const twitterDescription = seoInfo.twitterDescription
        ? seoInfo.twitterDescription
        : seoInfo.metaDesc
          ? seoInfo.metaDesc
          : cardCopy;

    const opengraphDescription = seoInfo.opengraphDescription
        ? seoInfo.opengraphDescription
        : seoInfo.metaDesc
          ? seoInfo.metaDesc
          : cardCopy;

    const twitterImage = seoInfo.twitterImage?.sourceUrl
        ? seoInfo.twitterImage.sourceUrl
        : featuredImage?.node?.sourceUrl;

    const openGraphImage = seoInfo.opengraphImage?.sourceUrl
        ? seoInfo.opengraphImage.sourceUrl
        : featuredImage?.node?.sourceUrl;

    const xDefault = find(urlPermutations, { languageCode: "en" });

    return (
        <>
            <html lang={languageCode} />
            <ErrorBoundary>
                <title>{title}</title>

                <meta
                    name="robots"
                    content={`${seoInfo?.metaRobotsNoindex}, ${seoInfo?.metaRobotsNofollow}`}
                />

                <link id="canonical" rel="canonical" href={url} />

                {map(urlPermutations, ({ languageCode, linkUrl }) => (
                    <link
                        key={languageCode}
                        rel="alternate"
                        hrefLang={languageCode}
                        href={`${siteUrl}${linkUrl}`}
                    />
                ))}

                {xDefault ? (
                    <link
                        rel="alternate"
                        hrefLang="x-default"
                        href={`${siteUrl}${xDefault.linkUrl}`}
                    />
                ) : null}

                {seoDescription ? (
                    <meta name="description" content={seoDescription} />
                ) : null}

                {/* OpenGraph tags */}
                <meta property="og:url" content={url} />

                {seoInfo.opengraphType ? (
                    <meta property="og:type" content={seoInfo.opengraphType} />
                ) : null}

                <meta
                    property="og:title"
                    content={
                        seoInfo.opengraphTitle ? seoInfo.opengraphTitle : title
                    }
                />

                {opengraphDescription ? (
                    <meta
                        property="og:description"
                        content={opengraphDescription}
                    />
                ) : null}

                {openGraphImage ? (
                    <meta property="og:image" content={openGraphImage} />
                ) : (
                    <meta property="og:image" content={companyLogo} />
                )}

                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />

                <meta
                    name="twitter:title"
                    content={
                        seoInfo.twitterTitle ? seoInfo.twitterTitle : title
                    }
                />

                {twitterDescription ? (
                    <meta
                        name="twitter:description"
                        content={twitterDescription}
                    />
                ) : null}

                {twitterImage ? (
                    <meta name="twitter:image" content={twitterImage} />
                ) : null}

                {isBrowser && !has(window, "IntersectionObserver") ? (
                    <Script src="https://polyfill.io/v3/polyfill.min.js?features=IntersectionObserver%2CSymbol%2CNode.prototype.contains" />
                ) : null}

                {showMultiviewCadModel ? (
                    <Script src="https://wolfspeed.componentsearchengine.com/common/plugin.js" />
                ) : null}
            </ErrorBoundary>
        </>
    );
};

Seo.propTypes = {
    urlPermutations: PropTypes.array,
    pageTitle: PropTypes.string,
    description: PropTypes.string,
    seo: PropTypes.shape({}),
    cardCopy: PropTypes.string,
    featuredImage: PropTypes.shape({}),
    productUrl: PropTypes.string,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }),
    languageCode: PropTypes.string,
};

export default Seo;
